import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Button } from './ui/Button';
import { SlidersHorizontal } from 'lucide-react';
interface SlidePressureProps {
  onContinue: () => void;
}
// Slide 2 — Portfolio Pressure Calculator
export function SlideLifecycle({
  onContinue
}: SlidePressureProps) {
  const [brands, setBrands] = useState(4);
  const [markets, setMarkets] = useState(16);
  const [campaigns, setCampaigns] = useState(4);
  const [animationDone, setAnimationDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimationDone(true), 1500);
    return () => clearTimeout(t);
  }, []);
  const moments = brands * markets * campaigns;
  const momentsSpring = useSpring(0, {
    bounce: 0,
    duration: 600
  });
  const displayMoments = useTransform(momentsSpring, latest => Math.round(latest).toLocaleString());
  useEffect(() => {
    momentsSpring.set(moments);
  }, [moments]);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-305">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-306">
        <span className="text-teal-400 text-xs uppercase tracking-widest font-medium" data-id="element-307">
          Portfolio Pressure
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-white mt-3 mb-4" data-id="element-308">
          Campaign complexity{' '}
          <span className="text-teal-400" data-id="element-309">multiplies fast.</span>
        </h2>
        <p className="text-lg text-white/60 max-w-2xl" data-id="element-310">
          This is the real workload — not campaigns, but campaign-country
          moments.
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mt-8" data-id="element-311">
        {/* LEFT — formula + sliders */}
        <div className="lg:col-span-7 space-y-6" data-id="element-312">
          {/* Formula display */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="bg-navy-900/50 border border-navy-600 rounded-2xl p-6" data-id="element-313">
            <p className="text-white/40 text-xs uppercase tracking-wider mb-4 font-medium" data-id="element-314">
              The formula
            </p>
            <div className="flex items-center justify-between gap-3 text-white" data-id="element-315">
              <div className="text-center flex-1" data-id="element-316">
                <div className="text-3xl md:text-4xl font-serif text-teal-400" data-id="element-317">
                  {brands}
                </div>
                <div className="text-xs text-white/50 mt-1" data-id="element-318">Brands</div>
              </div>
              <span className="text-2xl text-white/30" data-id="element-319">×</span>
              <div className="text-center flex-1" data-id="element-320">
                <div className="text-3xl md:text-4xl font-serif text-teal-400" data-id="element-321">
                  {markets}
                </div>
                <div className="text-xs text-white/50 mt-1" data-id="element-322">Markets</div>
              </div>
              <span className="text-2xl text-white/30" data-id="element-323">×</span>
              <div className="text-center flex-1" data-id="element-324">
                <div className="text-3xl md:text-4xl font-serif text-teal-400" data-id="element-325">
                  {campaigns}
                </div>
                <div className="text-xs text-white/50 mt-1" data-id="element-326">
                  Campaigns / brand / year
                </div>
              </div>
            </div>
          </motion.div>

          {/* Sliders */}
          <div className="bg-navy-900/70 p-6 rounded-2xl border border-navy-600 space-y-5 relative" data-id="element-327">
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: animationDone ? 1 : 0
          }} className="absolute -top-3 left-6" data-id="element-328">
              <span className="flex items-center gap-1.5 bg-teal-400/20 text-teal-300 px-2.5 py-1 rounded-full text-[10px] font-medium border border-teal-400/30" data-id="element-329">
                <SlidersHorizontal className="w-3 h-3" data-id="element-330" />
                Adjust the sliders
              </span>
            </motion.div>

            <div className="space-y-2 pt-1" data-id="element-331">
              <div className="flex justify-between text-sm" data-id="element-332">
                <label className="text-white/70" data-id="element-333">Priority brands</label>
                <span className="text-white font-medium" data-id="element-334">{brands}</span>
              </div>
              <input type="range" min="1" max="8" value={brands} onChange={e => setBrands(Number(e.target.value))} className="w-full" data-id="element-335" />
            </div>

            <div className="space-y-2" data-id="element-336">
              <div className="flex justify-between text-sm" data-id="element-337">
                <label className="text-white/70" data-id="element-338">West African markets</label>
                <span className="text-white font-medium" data-id="element-339">{markets}</span>
              </div>
              <input type="range" min="2" max="16" value={markets} onChange={e => setMarkets(Number(e.target.value))} className="w-full" data-id="element-340" />
            </div>

            <div className="space-y-2" data-id="element-341">
              <div className="flex justify-between text-sm" data-id="element-342">
                <label className="text-white/70" data-id="element-343">
                  Campaigns per brand / year
                </label>
                <span className="text-white font-medium" data-id="element-344">{campaigns}</span>
              </div>
              <input type="range" min="1" max="8" value={campaigns} onChange={e => setCampaigns(Number(e.target.value))} className="w-full" data-id="element-345" />
            </div>
          </div>
        </div>

        {/* RIGHT — Result */}
        <div className="lg:col-span-5 flex flex-col items-center lg:items-start" data-id="element-346">
          <p className="text-white/40 text-xs uppercase tracking-widest font-medium mb-3" data-id="element-347">
            = Campaign-country moments / year
          </p>
          <motion.div className="text-8xl md:text-9xl font-serif text-teal-400 tracking-tight leading-none" data-id="element-348">
            {displayMoments}
          </motion.div>
          <p className="text-white/60 leading-relaxed mt-6 border-l-2 border-teal-400/40 pl-4 py-1 max-w-sm" data-id="element-349">
            Each one needs strategy, adaptation, approval, launch, tracking and
            follow-up.
          </p>

          <Button onClick={onContinue} className="mt-8" data-id="element-350">
            See the hidden workload →
          </Button>
        </div>
      </div>
    </div>;
}